/**
 * Edit dialog for an IExperienceTemplateResourceSalesforce — admin picks
 * the SF object to upsert at experience launch, the external-ID field used
 * to match an existing record, an optional fallback external-ID field, and
 * the Dado → SF field mapping.
 *
 * Lives on the experience template (one or more SF resources per template,
 * parity with how Google Sheets resources work). On Save the dialog calls
 * `onChange(updatedResource)` so the parent wires it back into the
 * template's `resources` array; nothing is persisted to the org config doc.
 *
 * Mapping is split into two sections:
 *   - REQUIRED — the SF object's creation-blocking fields (LastName on
 *     Contact, Name on Account, …) plus the configured externalIdField. Each
 *     appears as a pre-pinned row keyed on the SF target; admin must pick a
 *     Dado source for each before saving.
 *   - OPTIONAL — additional Dado → SF mappings the admin wants to seed at
 *     launch (e.g. phone, ethnicity, birthdate). Free-form add/remove.
 *
 * Why split: SF rejects Contact creates without LastName ("Required fields
 * are missing: [LastName]") and the launch-time payload is built ONLY from
 * this fieldMap (subtask SF destinations don't contribute to the launch
 * upsert — they PATCH the existing Contact later). Splitting the form makes
 * the required-vs-optional contract obvious; pre-pinning the required SF
 * targets prevents the "I added LastName via a subtask, why is it failing?"
 * footgun.
 */

import { css } from '@emotion/css';
import styled from '@emotion/styled';
import {
  Button,
  Dialog,
  DialogContent,
  IconButton,
  MenuItem,
  TextField,
  Typography
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import shortid from 'shortid';

import { IExperienceTemplateResourceSalesforce } from '@dadohr/shared/domainTypes';
import { ButtonWithPromise } from '../../../../../../../../components/ButtonWithPromise';
import { DialogActionsWithSlots } from '../../../../../../../../components/DialogActionsWithSlots';
import { DialogTitleWithTypography } from '../../../../../../../../components/DialogTitleWithTypography';
import { useUnsafeTranslation } from '../../../../../../../../i18next';
import { useCurrentUser } from '../../../../../../../../services/currentUser';
import { useUserFields } from '../../../../../../../../services/user';
import {
  SalesforceFieldMeta,
  useSalesforceDataDestinationFields,
  useSalesforceObjects
} from '../../../../../../../Org/pages/Integrations/integrations/salesforce/service';

// ---------------------------------------------------------------------------
// Layout
// ---------------------------------------------------------------------------

const Section = styled('div')((p) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: p.theme.spacing(1.5)
}));

const SubSection = styled('div')((p) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: p.theme.spacing(1),
  padding: p.theme.spacing(1.5),
  border: `1px solid ${p.theme.palette.divider}`,
  borderRadius: 6
}));

const FieldMapRow = styled('div')((p) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr auto',
  alignItems: 'center',
  gap: p.theme.spacing(1)
}));

const FixedTargetRow = styled('div')((p) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  alignItems: 'center',
  gap: p.theme.spacing(1)
}));

// ---------------------------------------------------------------------------
// Required-field knowledge
//
// Hardcoded for the two SF stock objects we care about. Custom objects don't
// get auto-required fields here; the externalIdField is always required
// regardless of object (because the upsert key needs a source value).
//
// LastName on Contact is the only field SF *universally* refuses to create
// without. FirstName + Email are not strictly SF-required, but they are
// always-needed-in-practice for any participant upsert flow — we make them
// mandatory here so admins can't ship a Contact resource that drops the
// participant's identity at launch.
// ---------------------------------------------------------------------------

const REQUIRED_FIELDS_BY_OBJECT: Record<string, string[]> = {
  Contact: ['FirstName', 'LastName', 'Email'],
  Account: ['Name']
};

const getRequiredSfFields = (
  objectName: string,
  externalIdField: string
): string[] => {
  const base = REQUIRED_FIELDS_BY_OBJECT[objectName] ?? [];
  if (externalIdField && !base.includes(externalIdField)) {
    return [...base, externalIdField];
  }
  return base;
};

// Default Dado source guesses for the well-known Contact SF fields. Used to
// pre-seed the required-mapping rows when an admin first opens the dialog
// for a fresh Contact resource, so the common case ("map Dado firstName/
// lastName/email") is one click instead of three picker walks.
const DEFAULT_DADO_FOR_SF_FIELD: Record<string, string> = {
  FirstName: 'firstName',
  LastName: 'lastName',
  Email: 'email',
  Name: 'firstName'
};

// ---------------------------------------------------------------------------
// Local form state
// ---------------------------------------------------------------------------

type MappingRow = {
  id: string;
  dadoPath: string;
  sfField: string;
};

const emptyRow = (): MappingRow => ({ id: shortid(), dadoPath: '', sfField: '' });

// ---------------------------------------------------------------------------
// Field picker
// ---------------------------------------------------------------------------

const SfFieldSelect = ({
  fields,
  value,
  onChange,
  filter,
  label,
  disabled
}: {
  fields: SalesforceFieldMeta[];
  value: string;
  onChange: (next: string) => void;
  filter?: (f: SalesforceFieldMeta) => boolean;
  label: string;
  disabled?: boolean;
}) => {
  const visible = useMemo(
    () => (filter ? fields.filter(filter) : fields),
    [fields, filter]
  );
  return (
    <TextField
      select
      fullWidth
      label={label}
      value={value}
      disabled={disabled}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
        onChange(e.target.value)
      }
    >
      <MenuItem value="">—</MenuItem>
      {visible.map((f) => (
        <MenuItem key={f.name} value={f.name}>
          {f.label}
        </MenuItem>
      ))}
    </TextField>
  );
};

const DadoFieldSelect = ({
  userFields,
  value,
  onChange,
  label
}: {
  userFields: Array<{ key: string; label: string }>;
  value: string;
  onChange: (next: string) => void;
  label: string;
}) => {
  return (
    <TextField
      select
      fullWidth
      label={label}
      value={value}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
        onChange(e.target.value)
      }
    >
      <MenuItem value="">—</MenuItem>
      {userFields.map((f) => (
        <MenuItem key={f.key} value={f.key}>
          {f.label}
        </MenuItem>
      ))}
    </TextField>
  );
};

// ---------------------------------------------------------------------------
// Main dialog
// ---------------------------------------------------------------------------

export const EditSalesforceResourceDialog = ({
  open,
  onClose,
  orgId,
  value,
  onChange
}: {
  open: boolean;
  onClose: () => void;
  orgId: string;
  value: IExperienceTemplateResourceSalesforce;
  onChange: (next: IExperienceTemplateResourceSalesforce) => void;
}) => {
  const { t } = useUnsafeTranslation();
  const { org } = useCurrentUser();
  const userFields = useUserFields(org.customUserFields);

  const [objectName, setObjectName] = useState<string>(value.objectName || 'Contact');
  const [externalIdField, setExternalIdField] = useState<string>(value.externalIdField ?? '');
  const [fallbackExternalIdField, setFallbackExternalIdField] = useState<string>(
    value.fallbackExternalIdField ?? ''
  );

  // Required mappings live keyed by SF target — the SF side is fixed by the
  // object's contract; the admin only picks the Dado source.
  const [requiredDadoBySf, setRequiredDadoBySf] = useState<Record<string, string>>(
    () => {
      const initial: Record<string, string> = {};
      for (const [dadoPath, sf] of Object.entries(value.fieldMap || {})) {
        initial[sf] = dadoPath;
      }
      return initial;
    }
  );

  // Optional mappings are free-form rows (both sides editable).
  const [optionalRows, setOptionalRows] = useState<MappingRow[]>(() => {
    const initialRequired = new Set(
      getRequiredSfFields(value.objectName || 'Contact', value.externalIdField ?? '')
    );
    const optional = Object.entries(value.fieldMap || {}).filter(
      ([, sf]) => !initialRequired.has(sf)
    );
    if (optional.length === 0) return [emptyRow()];
    return optional.map(([dadoPath, sfField]) => ({
      id: shortid(),
      dadoPath,
      sfField
    }));
  });

  // Re-hydrate when the source resource doc changes (e.g. parent reordered
  // resources). Key by content, not by reference, so identical incoming data
  // doesn't reset the user's in-progress edits.
  const valueKey = JSON.stringify({
    objectName: value.objectName,
    externalIdField: value.externalIdField,
    fallbackExternalIdField: value.fallbackExternalIdField,
    fieldMap: value.fieldMap
  });
  useEffect(() => {
    setObjectName(value.objectName || 'Contact');
    setExternalIdField(value.externalIdField ?? '');
    setFallbackExternalIdField(value.fallbackExternalIdField ?? '');

    const nextRequired: Record<string, string> = {};
    for (const [dadoPath, sf] of Object.entries(value.fieldMap || {})) {
      nextRequired[sf] = dadoPath;
    }
    setRequiredDadoBySf(nextRequired);

    const initialRequired = new Set(
      getRequiredSfFields(value.objectName || 'Contact', value.externalIdField ?? '')
    );
    const optional = Object.entries(value.fieldMap || {}).filter(
      ([, sf]) => !initialRequired.has(sf)
    );
    setOptionalRows(
      optional.length > 0
        ? optional.map(([dadoPath, sfField]) => ({
            id: shortid(),
            dadoPath,
            sfField
          }))
        : [emptyRow()]
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueKey]);

  const requiredSfFields = useMemo(
    () => getRequiredSfFields(objectName, externalIdField),
    [objectName, externalIdField]
  );

  // Seed defaults for required rows that are still empty + freshly-mapped
  // (e.g., admin just picked an externalIdField that wasn't pinned before).
  useEffect(() => {
    setRequiredDadoBySf((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const sf of requiredSfFields) {
        if (!(sf in next)) {
          // Pre-seed a guess if the SF target has a canonical Dado source.
          const guess = DEFAULT_DADO_FOR_SF_FIELD[sf] ?? '';
          next[sf] = guess;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [requiredSfFields]);

  const [objects, objectsLoading] = useSalesforceObjects(orgId);
  const [fieldsResult, fieldsLoading] = useSalesforceDataDestinationFields(
    orgId,
    objectName
  );
  const fields = fieldsResult?.fields ?? [];

  const idLookupFields = useMemo(() => fields.filter((f) => f.idLookup), [fields]);

  const fieldsByApiName = useMemo(() => {
    const m = new Map<string, SalesforceFieldMeta>();
    for (const f of fields) m.set(f.name, f);
    return m;
  }, [fields]);

  // Validation. All required rows must have a non-empty Dado source.
  const missingRequired = useMemo(
    () => requiredSfFields.filter((sf) => !(requiredDadoBySf[sf] || '').trim()),
    [requiredSfFields, requiredDadoBySf]
  );

  // Surface a soft warning if an optional row maps the same SF target twice,
  // or overlaps a required row (the merged fieldMap can't have duplicate Dado
  // keys, so we'd silently overwrite).
  const optionalConflicts = useMemo(() => {
    const requiredDadoKeys = new Set(
      requiredSfFields
        .map((sf) => (requiredDadoBySf[sf] || '').trim())
        .filter(Boolean)
    );
    return optionalRows
      .filter((r) => {
        const dado = r.dadoPath.trim();
        return dado && requiredDadoKeys.has(dado);
      })
      .map((r) => r.dadoPath.trim());
  }, [optionalRows, requiredDadoBySf, requiredSfFields]);

  const canSave =
    !!objectName && !!externalIdField && missingRequired.length === 0;

  const onSave = async () => {
    const fieldMap: Record<string, string> = {};
    for (const sf of requiredSfFields) {
      const dado = (requiredDadoBySf[sf] || '').trim();
      if (dado) fieldMap[dado] = sf;
    }
    for (const row of optionalRows) {
      const dado = row.dadoPath.trim();
      const sf = row.sfField.trim();
      if (dado && sf) fieldMap[dado] = sf;
    }

    const next: IExperienceTemplateResourceSalesforce = {
      ...value,
      objectName,
      externalIdField,
      fallbackExternalIdField: fallbackExternalIdField || undefined,
      fieldMap
    };
    onChange(next);
    onClose();
  };

  // SF target label resolver — uses describe metadata when available, falls
  // back to the API name (humanised by SF's own label override otherwise).
  // Just the label here — matches the dropdown format the user picked from.
  const sfLabelFor = (apiName: string): string => {
    const meta = fieldsByApiName.get(apiName);
    return meta ? meta.label : apiName;
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitleWithTypography>
        {t('Salesforce data destination')}
      </DialogTitleWithTypography>
      <DialogContent>
        <Section>
          <Alert severity="info" icon={false}>
            {t(
              'When a participant enrolls, Dado upserts their record into the configured Salesforce object. Required fields below must be mapped — Salesforce rejects creates without them. Optional fields are sent if mapped and present on the participant.'
            )}
          </Alert>

          <TextField
            select
            label={t('Target object')}
            value={objectName}
            disabled={objectsLoading}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setObjectName(e.target.value);
              setExternalIdField('');
              setFallbackExternalIdField('');
            }}
            fullWidth
          >
            {(objects?.objects ?? [])
              .filter((o) => o.createable && o.updateable)
              .map((o) => (
                <MenuItem key={o.name} value={o.name}>
                  {o.label}
                </MenuItem>
              ))}
          </TextField>

          <SfFieldSelect
            fields={idLookupFields}
            value={externalIdField}
            onChange={setExternalIdField}
            label={t('External ID field (must be idLookup-enabled)')}
            disabled={fieldsLoading}
          />

          <SfFieldSelect
            fields={idLookupFields}
            value={fallbackExternalIdField}
            onChange={setFallbackExternalIdField}
            label={t('Fallback external ID field (optional)')}
            filter={(f) => f.name !== externalIdField}
            disabled={fieldsLoading}
          />

          {/* REQUIRED MAPPINGS */}
          <SubSection>
            <Typography variant="subtitle2">
              <strong>{t('Required field mappings')}</strong>
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {t(
                'Salesforce requires these fields to create or update a {{ object }}. Map each to a Dado field that is populated for every enrolled participant.',
                { object: objectName }
              )}
            </Typography>

            {requiredSfFields.length === 0 && (
              <Typography variant="body2" color="textSecondary">
                {t(
                  'Pick an External ID field above — Dado will require it to be mapped here.'
                )}
              </Typography>
            )}

            {requiredSfFields.map((sf) => (
              <FixedTargetRow key={sf}>
                <DadoFieldSelect
                  userFields={userFields}
                  value={requiredDadoBySf[sf] || ''}
                  onChange={(next) =>
                    setRequiredDadoBySf((prev) => ({ ...prev, [sf]: next }))
                  }
                  label={t('Dado field')}
                />
                <TextField
                  variant="outlined"
                  label={t('Salesforce field')}
                  value={sfLabelFor(sf)}
                  disabled
                  className={css`
                    .MuiInputBase-input {
                      color: rgba(0, 0, 0, 0.7) !important;
                    }
                  `}
                />
              </FixedTargetRow>
            ))}

            {missingRequired.length > 0 && (
              <Alert severity="warning" icon={false}>
                {t(
                  'Map a Dado field for: {{ fields }}. Salesforce will reject the upsert otherwise.',
                  { fields: missingRequired.join(', ') }
                )}
              </Alert>
            )}
          </SubSection>

          {/* OPTIONAL MAPPINGS */}
          <SubSection>
            <Typography variant="subtitle2">
              <strong>{t('Optional field mappings')}</strong>
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {t(
                'Additional Dado fields to seed onto the {{ object }} at launch. Subtask answers can also write to Salesforce fields after enrollment.',
                { object: objectName }
              )}
            </Typography>

            {optionalRows.map((row, idx) => (
              <FieldMapRow key={row.id}>
                <DadoFieldSelect
                  userFields={userFields}
                  value={row.dadoPath}
                  onChange={(next) =>
                    setOptionalRows((rs) =>
                      rs.map((r, i) => (i === idx ? { ...r, dadoPath: next } : r))
                    )
                  }
                  label={t('Dado field')}
                />
                <SfFieldSelect
                  fields={fields}
                  value={row.sfField}
                  onChange={(next) =>
                    setOptionalRows((rs) =>
                      rs.map((r, i) => (i === idx ? { ...r, sfField: next } : r))
                    )
                  }
                  label={t('Salesforce field')}
                  filter={(f) =>
                    (f.createable || f.updateable) &&
                    !requiredSfFields.includes(f.name)
                  }
                  disabled={fieldsLoading}
                />
                <IconButton
                  size="small"
                  onClick={() =>
                    setOptionalRows((rs) => rs.filter((_, i) => i !== idx))
                  }
                  aria-label={t('Remove field mapping')}
                >
                  <X size={16} />
                </IconButton>
              </FieldMapRow>
            ))}
            <Button
              size="small"
              color="primary"
              onClick={() => setOptionalRows((rs) => [...rs, emptyRow()])}
            >
              {t('Add field mapping')}
            </Button>

            {optionalConflicts.length > 0 && (
              <Alert severity="warning" icon={false}>
                {t(
                  'Optional mapping(s) overlap a required mapping (same Dado field): {{ fields }}. The required mapping wins; remove the duplicate row.',
                  { fields: optionalConflicts.join(', ') }
                )}
              </Alert>
            )}
          </SubSection>
        </Section>
      </DialogContent>
      <DialogActionsWithSlots
        right={
          <>
            <Button onClick={onClose}>{t('Cancel')}</Button>
            <ButtonWithPromise
              variant="contained"
              color="primary"
              disabled={!canSave}
              onClick={onSave}
              pending={`${t('Saving')}...`}
            >
              {t('Save')}
            </ButtonWithPromise>
          </>
        }
      />
    </Dialog>
  );
};
